import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import { ifNotProp, ifProp, palette } from 'styled-tools';

import {
	Button,
	FontSizes,
	FontWeights,
	FormInput,
	minWidth,
	QuestionnaireTile,
	Text,
	units,
} from '@calm-web/design-system';

export const Title = styled(Text).attrs({
	weight: FontWeights.Regular,
	color: 'gray7',
	styleAs: 'h4',
	el: 'h1',
})<{
	hasMargin: boolean;
}>`
	margin-bottom: ${(props): string => (props.hasMargin ? '40px' : '8px')};
	margin-top: 16px;
	outline: none;
	${minWidth('tablet')} {
		margin-top: 0;
	}
`;

export const B2BTitle = styled(Text).attrs({
	size: FontSizes.lg,
	weight: FontWeights.Regular,
	color: 'white',
	el: 'h1',
})`
	margin-bottom: ${units(1)};
`;

export const Subtitle = styled.p`
	color: ${palette('gray8')};
	font-size: 16px;
	margin-bottom: 24px;
`;

export const B2BSubtitle = styled(Text).attrs({
	size: FontSizes.base,
	weight: FontWeights.Regular,
	color: 'white',
	el: 'h4',
})`
	margin-bottom: ${units(3)};
	outline: none;
`;

export const Choices = styled.div`
	align-items: stretch;
	display: flex;
	flex-direction: column;
	position: relative;
`;

export const Choice = styled(QuestionnaireTile)<{
	isB2B?: boolean;
	$transitionDelay: number;
	$isEntering: boolean;
}>`
	opacity: ${ifProp('$isEntering', 0, 1)};
	transition-delay: ${props => props.$transitionDelay}ms;
	transform: ${ifProp('$isEntering', 'translateY(8px)', 'translateY(0)')};
	${props =>
		props.isB2B &&
		`
		background: ${props.isSelected ? rgba(palette('black')(props), 0.6) : rgba(palette('black')(props), 0.3)};
		border-color: ${props.isSelected ? 'white' : 'transparent'};
		color: white;
		transition-delay: unset;
		transform: unset;
		`}
`;

export const AnimatedContainer = styled.div<{
	isVisible: boolean;
	isSelected: boolean;
	shouldAnimate: boolean;
}>`
	opacity: ${(props): number => (props.isVisible ? 1 : 0)};
	${(props): string => (props.isVisible ? 'margin-bottom: 8px; max-height: 140px;' : 'max-height: 0')};
	visibility: ${(props): string => (props.isVisible ? 'visible' : 'hidden')};
	${(props): string =>
		props.shouldAnimate
			? `
        transition: 300ms ease-in-out;
        transition-delay: 200ms;
      `
			: ''}
`;

export const AnimatedResponse = styled.div<{
	isB2B?: boolean;
	isVisible: boolean;
	shouldAnimate: boolean;
	compact?: boolean;
}>`
	opacity: ${ifProp('isVisible', '1', '0')};
	position: relative;
	${props =>
		props.isB2B
			? `
		height: 100%;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		`
			: `
		height: ${ifProp('isVisible', 'auto', '0')};
	`}

	${(props): string =>
		props.shouldAnimate
			? `
      transition: 300ms ease-in-out;
      transition-delay: 500ms;
    `
			: ''}
	& p {
		margin-bottom: 40px;
		&:last-of-type {
			margin-bottom: 0;
		}
	}
`;

export const NavButtonsWrapper = styled.div<{
	isB2B?: boolean;
	isVisible: boolean;
	shouldAnimate: boolean;
	compact?: boolean;
}>`
	display: flex;
	${props =>
		props.isB2B
			? ''
			: `
			opacity: ${props.isVisible ? '1' : '0'};
			transform: ${props.isVisible ? 'translate3d(0, 0, 0)' : 'translate3d(0, 8px, 0)'};
			visibility: ${props.isVisible ? 'visible' : 'hidden'};
			flex-grow: 1;
			margin-top: ${units(2)};
			`}
	width: 100%;
	${ifNotProp(
		'isVisible',
		ifProp(
			'compact',
			css`
				display: none;
			`,
		),
	)}
	${(props): string =>
		props.shouldAnimate
			? `
        transition: 300ms ease-in-out;
        transition-delay: 500ms;
      `
			: ''}
  ${minWidth('desktopLarge')} {
		position: static;
	}
	${ifProp(
		'compact',
		css`
			position: relative;
		`,
	)}
`;

export const PollBodyContainer = styled.div<{ isB2B?: boolean }>`
	${props => (props.isB2B ? 'margin-bottom: 20px;' : '')};
`;

export const NextButton = styled(Button)<{ compact?: boolean }>`
	margin-top: auto;
	margin-bottom: 16px;

	${ifProp(
		'compact',
		css`
			background: ${palette('blue2')};
			color: ${palette('white')};
		`,
	)}
`;

export const BackButton = styled(Button)<{ isDisabled: boolean | undefined }>`
	${props => props.isDisabled && 'display: none;'}
	background: ${props => rgba(palette('black')(props), 0.2)};
	color: white;
	margin-top: auto;
	margin-right: ${units(1)};
	margin-bottom: 16px;
`;

export const TextArea = styled(FormInput)`
	& > div {
		background-color: ${props => rgba(palette('black')(props), 0.3)};
	}

	input {
		color: ${palette('white')};
		border: 1px solid ${props => rgba(palette('white')(props), 0.4)};
	}
`;
